import { ListItem, ListItemButton } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';

// Custom shouldForwardProp replaces MUI's default — keep filtering sx/ownerState/theme/as
// or `sx={{…}}` is serialized onto the DOM as "[object Object]" and never becomes CSS.
const muiForward = (prop) => prop !== 'ownerState' && prop !== 'theme' && prop !== 'sx' && prop !== 'as';

const listForward = (prop) =>
  muiForward(prop) && !['inset', 'maxWidth', 'zebra', 'zebraMode'].includes(prop);

const rowForward = (prop) =>
  muiForward(prop) &&
  ![
    'header',
    'dimmed',
    'striped',
    'rowVariant',
    'fullBleed',
    'button',
    'layout',
    'dense',
    'hover',
    'clickable',
    'px',
    'listItem',
    'alignItems',
  ].includes(prop);

const cellForward = (prop) =>
  muiForward(prop) && !['width', 'flexGrow', 'hideBelow', 'stackAt', 'alignItems', 'minWidth'].includes(prop);

const cssZebra = (theme) => ({
  '& > *:nth-child(even)': {
    backgroundColor: theme.palette.action.disabledBackground,
  },
});

/**
 * Alternating-row list container. Default zebra uses CSS nth-child (assignment ZebraList SOT).
 * Use zebraMode="prop" when CSS zebra is wrong (DnD reorder, rowVariant overlays).
 */
export const FlexGridListRoot = styled('div', {
  shouldForwardProp: listForward,
})(({ theme, inset, maxWidth, zebra = true, zebraMode = 'css' }) => {
  const hasInset = inset != null && inset !== 0;
  return {
    // inset uses marginLeft for visual indent — subtract it so width:100% does not overflow
    // the modal (regression vs pre-FlexGrid MarginLeftZebraList block flow).
    width: hasInset ? `calc(100% - ${theme.spacing(inset)})` : '100%',
    boxSizing: 'border-box',
    minWidth: 0,
    ...(maxWidth
      ? {
          maxWidth,
          marginLeft: 'auto',
          marginRight: 'auto',
        }
      : null),
    ...(hasInset ? { marginLeft: theme.spacing(inset) } : null),
    ...(zebra && zebraMode === 'css' ? cssZebra(theme) : null),
  };
});

const rowStyles = ({
  theme,
  header,
  dimmed,
  striped,
  rowVariant,
  fullBleed,
  layout,
  dense,
  hover,
  clickable,
  px,
  listItem,
  button,
  alignItems,
}) => ({
  position: 'relative',
  boxSizing: 'border-box',
  // Shared row density for Phase 1 lists + admin DataTable (override via sx when needed).
  minHeight: 48,
  // Assignment / plain div rows: vertical padding. ListItem / ListItemButton keep MUI gutters.
  ...(!(listItem || button)
    ? {
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
      }
    : null),
  ...(px != null
    ? {
        paddingLeft: theme.spacing(px),
        paddingRight: theme.spacing(px),
      }
    : null),
  ...(layout === 'columns'
    ? {
        display: 'flex',
        flexDirection: 'row',
        alignItems: alignItems || 'center',
        width: '100%',
        minWidth: 0,
      }
    : {
        // Block rows (assignment lists): still fill the list so marginLeft:auto checkboxes stay in view.
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
      }),
  ...(dense ? { fontSize: 13 } : null),
  ...(clickable ? { cursor: 'pointer' } : null),
  ...(hover
    ? {
        '&:hover': {
          opacity: 0.7,
        },
      }
    : null),
  ...(fullBleed
    ? {
        marginLeft: -20,
        paddingLeft: 20,
      }
    : null),
  ...(dimmed ? { opacity: 0.5 } : null),
  ...(header ? { borderBottom: `1px solid ${theme.palette.divider}` } : null),
  ...(striped ? { backgroundColor: theme.palette.action.disabledBackground } : null),
  ...(rowVariant === 'new'
    ? {
        backgroundColor: striped
          ? alpha(theme.palette.error.main, 0.4)
          : alpha(theme.palette.error.main, 0.2),
      }
    : null),
});

export const FlexGridRowRoot = styled('div', {
  shouldForwardProp: rowForward,
})(rowStyles);

export const FlexGridRowListItem = styled(ListItem, {
  shouldForwardProp: rowForward,
})(rowStyles);

export const FlexGridRowListItemButton = styled(ListItemButton, {
  shouldForwardProp: rowForward,
})(rowStyles);

export const FlexGridCellRoot = styled('div', {
  shouldForwardProp: cellForward,
})(({ theme, width, flexGrow, hideBelow, stackAt, alignItems, minWidth }) => ({
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: alignItems || 'center',
  overflow: 'hidden',
  wordBreak: 'break-word',
  minWidth: minWidth ?? 0,
  // Fixed basis keeps column widths stable across rows (content no longer stretches cells).
  ...(width != null && flexGrow == null
    ? {
        width,
        flex: `0 0 ${width}`,
        maxWidth: width,
      }
    : null),
  ...(flexGrow != null
    ? {
        flexGrow,
        flexShrink: 1,
        flexBasis: width != null ? width : 0,
        ...(width != null ? { width } : null),
      }
    : null),
  ...(minWidth
    ? {
        [theme.breakpoints.down('sm')]: {
          width: minWidth,
          flex: `0 0 ${minWidth}`,
          maxWidth: minWidth,
        },
      }
    : null),
  ...(hideBelow
    ? {
        [theme.breakpoints.down(hideBelow)]: {
          display: 'none',
        },
      }
    : null),
  ...(stackAt
    ? {
        [theme.breakpoints.down(stackAt)]: {
          width: '100%',
          flex: '1 1 100%',
          maxWidth: '100%',
          flexDirection: 'column',
          alignItems: 'flex-start',
        },
      }
    : null),
}));

export const FlexGridCheckboxCellRoot = styled('div')({
  width: 36,
  flex: '0 0 36px',
  flexShrink: 0,
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
});
